import Highcharts from 'highcharts';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, Subject } from 'rxjs';
import deepEqual from 'Angular/utils/equal/deep-equal';
const DEFAULT_FONT = 'Roboto Condensed, sans-serif';
/**
 * ChartZoomService
 * A Service that updates a Highchart to control its zoom and panning options
 *
 * Usage Example:
 * this.highchartOptions = this.chartZoomService.addZoomOptions(this.highchartOptions);
 * this.chartZoomService.setChart(this.ychartFundInteractive.stockChart);
 * this.chartZoomService.zoomIn(axisData);
 */
export class ChartZoomService {
    constructor() {
        this.chart = null;
        this.zoomedIn = false;
        this.panningOn = false;
        this.originalFont = '';
        this.axisZoomExtremesSubject = new Subject();
        this.zoomUpdated$ = this.axisZoomExtremesSubject.asObservable();
        this.zoomSubject = new Subject();
        this.zoomEvent$ = this.zoomSubject.asObservable();
        this.xAxisZoomExtremes = null;
        this.yAxisZoomExtremes = null;
        this.widthSubject = new BehaviorSubject(0);
        this.widthUpdated$ = this.widthSubject.asObservable();
    }
    setChart(chart) {
        this.chart = chart;
        this.zoomedIn = false;
        this.redraw();
        this.zoomEventEmit();
    }
    getZoomEvent() {
        return this.zoomEvent$;
    }
    clearChart() {
        this.chart = null;
        this.zoomEventEmit();
    }
    getChartWidthChange() {
        return this.widthUpdated$.pipe(distinctUntilChanged());
    }
    getAxisExtremes() {
        return this.zoomUpdated$.pipe(debounceTime(500), // afterSetExtremes can emit in quick bursts, especially while panning.
        filter((axisExtremes) => {
            // We only care about axisExtremes in 2 scenarios:
            //   1. All values are undefined. This means the chart is zoomed out all the way
            //   2. All values are defined. This means the chart is zoomed in
            // In the scenario where some are defined and some are undefined, the chart is in the process of
            // zooming so we want to filter these values out.
            //
            // NOTE: Use `value !== undefined` (not `!!value`) on the fully-specified
            // branch so a zero extreme — a legitimate, finite axis boundary — is not
            // silently discarded as falsy. Matches the `=== undefined` pattern on the
            // first branch and the map step below.
            const extremeValues = [
                axisExtremes?.x.max,
                axisExtremes?.x.min,
                axisExtremes?.y.max,
                axisExtremes?.y.min,
            ];
            if (extremeValues.every((value) => value === undefined))
                return true;
            return extremeValues.every((value) => value !== undefined);
        }), map((axisExtremes) => {
            // If all values are undefined, return the empty object because that is how we represent a
            // zoomed out chart.
            const extremeValues = [
                axisExtremes?.x.max,
                axisExtremes?.x.min,
                axisExtremes?.y.max,
                axisExtremes?.y.min,
            ];
            if (extremeValues.every((value) => value === undefined))
                return {};
            return axisExtremes;
        }));
    }
    /**
     * Get the options from a Chart and return it with needed zoom/pan properties.
     * Must be called before creating your Chart so these are already in palce.
     *
     * @param {T} chartOpts - Types that extend from Highcharts.Option
     * @return {*}  {T} Returns same modified options
     * @memberof ChartZoomService
     */
    addZoomOptions(chartOpts) {
        this.originalFont = chartOpts.chart?.style?.fontFamily ?? DEFAULT_FONT;
        const axisEvents = {
            xAxis: {
                events: {
                    afterSetExtremes: (axisExtremes) => {
                        this.xAxisZoomExtremes = axisExtremes;
                        this.afterSetExtremes();
                    },
                },
            },
            yAxis: {
                events: {
                    afterSetExtremes: (axisExtremes) => {
                        this.yAxisZoomExtremes = axisExtremes;
                        const { min, max } = axisExtremes;
                        const yAxisExists = this.chart && 'yAxis' in this.chart && this.chart.yAxis.length > 0;
                        if (yAxisExists && this.chart?.yAxis[0].min === min && this.chart?.yAxis[0].max === max) {
                            this.afterSetExtremes();
                        }
                    },
                },
            },
        };
        return Highcharts.merge(chartOpts, axisEvents);
    }
    /**
     * Deserialize provided URL state to zoom into Chart
     *
     * @param {string} source - Containing axisExtremes with URL serialized state data
     * @return {*}  {void}
     * @memberof ChartZoomService
     */
    zoomIn(axisExtremes) {
        if (this.chart === null || axisExtremes === undefined || deepEqual(axisExtremes, {}))
            return;
        if (!deepEqual(axisExtremes, {})) {
            const { x, y } = axisExtremes;
            this.zoomedIn = true;
            this.zoomEventEmit();
            if (this.chart.xAxis.length > 0) {
                this.chart.xAxis[0].setExtremes(x.min, x.max, false);
            }
            if (this.chart.yAxis.length > 0) {
                this.chart.yAxis[0].setExtremes(y.min, y.max, true);
            }
        }
    }
    zoomOut() {
        if (!this.chart)
            return;
        this.chart.zoomOut();
        this.zoomedIn = false;
        this.zoomEventEmit();
    }
    zoomEventEmit() {
        const { panningOn, zoomedIn } = this;
        this.zoomSubject.next({ enabled: this.chart !== null, zoomedIn, panningOn });
    }
    /**
     * Toggle between panning and zooming in the Chart
     * @param enabled - toggle panning in Chart
     */
    updatePanning(enabled) {
        if (!this.chart)
            return;
        this.panningOn = enabled;
        this.zoomEventEmit();
        const chartOptions = {
            chart: {
                height: null,
                panning: {
                    enabled,
                    type: 'xy',
                },
                style: {
                    cursor: enabled ? 'move' : 'default',
                    fontFamily: enabled ? '' : this.originalFont, // Reapply the font that was removed
                },
                zooming: {
                    type: enabled ? undefined : 'xy',
                },
            },
        };
        this.chart.update(chartOptions);
    }
    afterSetExtremes() {
        // Highcharts types userMin/userMax as `number | undefined`, but at runtime they can be
        // non-numeric truthy values (e.g. Date objects on datetime axes), which crashes
        // `.toFixed()`. Guard with an explicit `typeof === 'number'` check so non-numeric
        // values fall through to `undefined` (the zoomed-out semantic the rest of this
        // service expects).
        const zoomData = {
            x: {
                min: typeof this.xAxisZoomExtremes?.userMin === 'number'
                    ? Number(this.xAxisZoomExtremes.userMin.toFixed(4))
                    : undefined,
                max: typeof this.xAxisZoomExtremes?.userMax === 'number'
                    ? Number(this.xAxisZoomExtremes.userMax.toFixed(4))
                    : undefined,
            },
            y: {
                min: typeof this.yAxisZoomExtremes?.userMin === 'number'
                    ? Number(this.yAxisZoomExtremes.userMin.toFixed(4))
                    : undefined,
                max: typeof this.yAxisZoomExtremes?.userMax === 'number'
                    ? Number(this.yAxisZoomExtremes.userMax.toFixed(4))
                    : undefined,
            },
        };
        const extremeValues = [zoomData.x.max, zoomData.x.min, zoomData.y.max, zoomData.y.min];
        this.zoomedIn = !extremeValues.every((value) => value === undefined);
        this.zoomEventEmit();
        this.axisZoomExtremesSubject.next(zoomData);
    }
    /**
     * Get Chart sizing data, calculate left padding between chart and plot
     *
     * @return {*}  {void}
     * @memberof ChartZoomService
     */
    redraw() {
        if (!this.chart)
            return;
        const { marginRight } = this.chart;
        const chartPadding = marginRight ?? 0;
        this.widthSubject.next(chartPadding);
    }
}
